import { type Extension, type Tournament } from '@clubspark-node/clubspark-tennis-types';
import { deepEqual } from 'fast-equals';
import { atom, type PrimitiveAtom } from 'jotai';
import { atomFamily, selectAtom } from 'jotai/utils';

import { type EventFormat } from 'components/dialogs/draw/addDraw/utils';
import { client } from 'config/graphql/client';
import { GET_LEVEL_CONFIG } from 'config/graphql/queries';
import { type Configuration } from 'typedefs/event';
import { factorySafeTimeToDayjs, zonedDayjsDate } from 'utils/dateTimeUtils';
import { isStandalone } from 'utils/environment';

const hydrateTournament = (tournament: Tournament): Tournament => ({
  ...tournament,
  ...(tournament && {
    startDate: factorySafeTimeToDayjs(
      tournament.startDate ?? new Date().toISOString(),
      tournament.localTimeZone,
    ).toDate(),
    endDate: factorySafeTimeToDayjs(tournament.endDate ?? new Date().toISOString(), tournament.localTimeZone).toDate(),
  }),
});

export const tournamentIdAtom = atom(undefined) as PrimitiveAtom<string | undefined>;

export const tournamentsAtom = atom<Record<string, Tournament>>({});

const readOnlyTournamentAtom = selectAtom(
  atom((get) => ({ tournaments: get(tournamentsAtom), tournamentId: get(tournamentIdAtom) })),
  ({ tournaments, tournamentId }) => tournaments?.[tournamentId],
  deepEqual,
);

export const tournamentAtom = atom<Tournament, [Tournament], void>(
  (get) => hydrateTournament(get(readOnlyTournamentAtom)),
  (get, set, tournamentRecord) => {
    const tournaments = get(tournamentsAtom);
    if (deepEqual(tournaments[tournamentRecord?.tournamentId], tournamentRecord)) return;
    set(tournamentsAtom, { ...tournaments, [tournamentRecord?.tournamentId]: tournamentRecord });
  },
);

export const tournamentNameAtom = selectAtom(tournamentAtom, (tournament) => tournament?.tournamentName ?? '');

export const tournamentsIdNameAtom = selectAtom(tournamentsAtom, (tournaments) =>
  Object.entries(tournaments).map(([tournamentId, tournament]) => ({
    tournamentId,
    tournamentName: tournament?.tournamentName,
  })),
);

// Level config query requires tournament to be imported in the backend. We wait for
// tournamentId atom to reslove to ensure we do not call prior to import completing.
export const tournamentLevelConfigAtomFamily = atomFamily((tournamentId: string) =>
  atom(async () => {
    if (!tournamentId) return null;
    const { data } = await client.query({
      query: GET_LEVEL_CONFIG,
      fetchPolicy: isStandalone() ? 'cache-only' : 'cache-first',
      variables: { tournamentId },
    });
    return data?.tournamentLevelConfig;
  }),
);

export const tournamentLevelConfigAtom = atom((get) => {
  const tournamentId = get(tournamentIdAtom);
  if (!tournamentId) return null;
  return get(tournamentLevelConfigAtomFamily(tournamentId));
});

export interface LevelConfiguration {
  allowBackdrawMovements: boolean | null;
  allowGeneratePlayOffForBackDraws: boolean | null;
  allowQualifyingDraws: boolean | null;
  allowSplitDraws: boolean | null;
  allowTeamSubstitutions: boolean | null;
  allowWildcards: boolean | null;
  doublesEventFormats: EventFormat[] | null;
  doublesScoreFormats: string[] | null;
  eventFormats: EventFormat[] | null;
  firstMatchLoserConsolation: boolean | null;
  goldSilverBronzeDrawsEnabled: boolean | null;
  ntrpEnabled: boolean | null;
  playTracker: boolean | null;
  rankingsEnabled: boolean | null;
  scoreFormats: string[] | null;
  splitDrawEventFormats: EventFormat[] | null;
  roundRobinPlayoffDrawEventFormats: EventFormat[] | null;
  requireSafeplayApproval: boolean | null;
  teamScoreFormats: string[] | null;
  wtnRankingsEnabled: boolean | null;
}

export interface LevelExtension extends Extension {
  name: 'level';
  value: LevelConfiguration & {
    currentOrgLevelConfiguration: LevelConfiguration;
    levelConfigurationOverrides: Partial<LevelConfiguration>;
    level: {
      id: string;
      category: string;
      isRanking: boolean;
      locked: boolean;
      name: string;
      onlineRegistrationMandatory: boolean;
      orderIndex: number;
      shortName: string;
      configuration: Configuration;
    };
  };
}

export const levelExtensionAtom = selectAtom<Tournament, LevelExtension['value']>(
  tournamentAtom,
  (t) => t?.extensions?.find((e) => e.name === 'level')?.value || {},
  deepEqual,
);

export interface UpcomingMatchUpSortOrderExtension extends Extension {
  name: 'upcomingMatchUpSortOrder';
  value: {
    sortOrder: number;
    matchUpId: string;
  }[];
}

export const upcomingMatchUpsExtensionAtom = selectAtom<Tournament, UpcomingMatchUpSortOrderExtension['value']>(
  tournamentAtom,
  (t) => t?.extensions?.find((e) => e.name === 'upcomingMatchUpSortOrder')?.value ?? [],
  deepEqual,
);

export interface UnScheduledMatchUpSortOrderExtension extends Extension {
  name: 'unScheduledMatchUpSortOrder';
  value: {
    sortOrder: number;
    matchUpId: string;
  }[];
}

export const unScheduledMatchUpsExtensionAtom = selectAtom<Tournament, UnScheduledMatchUpSortOrderExtension['value']>(
  tournamentAtom,
  (t) => t?.extensions?.find((e) => e.name === 'unScheduledMatchUpSortOrder')?.value ?? [],
  deepEqual,
);

export const isDualMatchAtom = selectAtom<any, boolean>(
  tournamentAtom,
  (t) => t?.dualMatchMetadata?.[0]?.dualMatchIdType === 'TOURNAMENT',
);

export const tournamentSeasonIdAtom = selectAtom<Tournament, string | undefined>(tournamentAtom, (t) => t?.season);

export const localTimeZoneAtom = selectAtom(tournamentAtom, (t) => t?.localTimeZone);

export const tournamentStartDateAtom = selectAtom(tournamentAtom, (t) =>
  zonedDayjsDate(t?.startDate, t?.localTimeZone),
);
export const tournamentEndDateAtom = selectAtom(tournamentAtom, (t) => zonedDayjsDate(t?.endDate, t?.localTimeZone));

export const tournamentVenuesAtom = selectAtom(tournamentAtom, (t) => t?.venues);

export const tournamentParentOrganisationAtom = selectAtom(tournamentAtom, (t) => t?.parentOrganisation);
